import { t } from 'i18next';
import { useEffect, useState } from 'react';

import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import {
  CurrentPlanResponse,
  studioCreditApi,
} from 'src/services/api/studioCreditApi';
import { AppDispatch } from 'src/store/configureStore';
import { alertSnackbar } from 'src/store/ui/actions';
import { millicreditsToCredits } from 'src/utils/studioCredits';

import { getStudioPlanTitle } from './studioFeatureGating';

const COMPLETED_ACTION_PARAM = 'completedAction';
const STUDIO_UPGRADE_ACTION = 'studioUpgrade';
const UPGRADE_PLAN_PARAM = 'upgradePlan';
const UPGRADE_CREDITS_PARAM = 'upgradeCredits';

// The checkout completion webhook performs the actual upgrade, so on return
// from Stripe the new plan can lag by a few webhook round-trips. The chain runs
// base-price swap → extra-credits item → credit reset, and the wait below is
// for the LAST of those, so the ceiling covers the whole chain rather than its
// first step.
const UPGRADE_POLL_INTERVAL_MS = 2000;
const UPGRADE_POLL_MAX_ATTEMPTS = 15;

/** What the customer bought, carried across the redirect to be waited for. */
export interface StudioUpgradeExpectation {
  plan: string;
  credits: number;
}

/**
 * The return URL a checkout-setup upgrade sends the customer back to — the page
 * they started on, marked so {@link useStudioUpgradeCheckoutReturn} recognises
 * the return. The purchased selection rides along because the confirmation
 * waits for the upgrade to match it, and the redirect to Stripe discards
 * everything held in memory.
 */
export const studioUpgradeReturnUrl = (
  pageUrl: string,
  expectation: StudioUpgradeExpectation,
): string => {
  const url = new URL(pageUrl);
  url.searchParams.set(COMPLETED_ACTION_PARAM, STUDIO_UPGRADE_ACTION);
  url.searchParams.set(UPGRADE_PLAN_PARAM, expectation.plan);
  url.searchParams.set(UPGRADE_CREDITS_PARAM, String(expectation.credits));
  return url.toString();
};

// Read through `history` rather than `window.location` so the marker is read
// from the same place it is stripped on.
const isStudioUpgradeReturn = (): boolean =>
  new URLSearchParams(history.location?.search ?? '').get(
    COMPLETED_ACTION_PARAM,
  ) === STUDIO_UPGRADE_ACTION;

// Undefined for a return URL minted before the selection was carried (a
// checkout started by an older client and completed after a deploy), which
// falls back to the weaker "any paid plan" signal below.
const readExpectation = (): StudioUpgradeExpectation | undefined => {
  const params = new URLSearchParams(history.location.search);
  const plan = params.get(UPGRADE_PLAN_PARAM);
  const credits = Number(params.get(UPGRADE_CREDITS_PARAM));
  if (!plan || !Number.isFinite(credits) || credits <= 0) {
    return undefined;
  }
  return { plan, credits };
};

// Handled once per page load rather than once per mounted hook: the return
// belongs to the page Stripe landed on, and several surfaces on it may mount
// this — they must not each poll and toast.
let hasHandledReturn = false;

const isPaidStudioPlan = (plan?: CurrentPlanResponse): boolean =>
  plan?.family === 'studio' &&
  plan.plan !== undefined &&
  plan.plan !== 'freemium';

/**
 * Polls the store directly instead of through the claiming component's query
 * subscription, and is deliberately never cancelled: claiming the return
 * consumes the URL marker, so nothing can retry it, and the surface that
 * claimed it may well go away first (the sidebar card unmounts on navigation).
 * Whoever starts this owes the user one of the two messages below.
 *
 * The plan AND the balance are refetched every attempt, and the wait is for
 * both to match what was bought. Settling for "the plan is no longer freemium"
 * returned mid-chain — true the moment the base price swaps, seconds before the
 * extra-credits item exists and the credits land — and the fetch that saw that
 * intermediate state was the last one, leaving the stale figures on screen for
 * the rest of the session.
 */
export const confirmStudioUpgrade = async (
  dispatch: AppDispatch,
  expectation: StudioUpgradeExpectation | undefined,
) => {
  for (let attempt = 0; attempt < UPGRADE_POLL_MAX_ATTEMPTS; attempt++) {
    const [{ data: plan }, { data: balance }] = await Promise.all([
      dispatch(
        studioCreditApi.endpoints.getCurrentPlan.initiate(undefined, {
          subscribe: false,
          forceRefetch: true,
        }),
      ),
      dispatch(
        studioCreditApi.endpoints.getCreditBalance.initiate(undefined, {
          subscribe: false,
          forceRefetch: true,
        }),
      ),
    ]);
    // checkout-setup only serves the Freemium→paid conversion, so any resolved
    // paid Studio plan means the swap landed; with a known selection the wait
    // continues until the credit total is on the subscription and the ledger
    // has been re-granted to it.
    const landed =
      expectation === undefined
        ? isPaidStudioPlan(plan)
        : plan?.plan === expectation.plan &&
          plan?.credits === expectation.credits &&
          balance !== undefined &&
          millicreditsToCredits(balance.monthlyCreditGrant) ===
            expectation.credits;
    if (landed) {
      dispatch(
        alertSnackbar({
          successMessage: t(
            ($) => $.page_studio_plans.plan_change.upgrade_success,
            { plan: getStudioPlanTitle(plan?.plan) },
          ),
        }),
      );
      return;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, UPGRADE_POLL_INTERVAL_MS);
    });
  }
  dispatch(
    alertSnackbar({
      successMessage: t(
        ($) => $.page_studio_plans.plan_change.upgrade_confirming,
      ),
    }),
  );
};

/**
 * Confirms an upgrade that completed through Stripe checkout. Checkout returns
 * with a full page load, so this runs on mount: it polls the current plan until
 * the completion webhook has applied the change, then reports it.
 *
 * Every surface that can start a checkout-setup upgrade should mount this on the
 * pages it returns to — the sidebar CTA and the plans page are both entry
 * points, and only whichever is mounted on the return page can confirm it.
 */
export const useStudioUpgradeCheckoutReturn = () => {
  const dispatch = useAppDispatch();
  const [isConfirming, setIsConfirming] = useState(false);
  const isReturnOnThisRender = isStudioUpgradeReturn();

  useEffect(() => {
    // Every other page load is a non-return, and costs no request at all.
    if (hasHandledReturn || !isStudioUpgradeReturn()) {
      return;
    }
    hasHandledReturn = true;
    setIsConfirming(true);
    const expectation = readExpectation();
    // Strip the params so a refresh doesn't re-poll.
    const params = new URLSearchParams(history.location.search);
    params.delete(COMPLETED_ACTION_PARAM);
    params.delete(UPGRADE_PLAN_PARAM);
    params.delete(UPGRADE_CREDITS_PARAM);
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });

    void confirmStudioUpgrade(dispatch, expectation).finally(() => {
      setIsConfirming(false);
    });
  }, [dispatch]);

  // The URL marker is true on the first render, before the effect can move the
  // pending state into React. Callers can therefore hold interrupted work from
  // the first paint until checkout confirmation finishes.
  return isReturnOnThisRender || isConfirming;
};
